/**
 * B080080 バッチ関連照会
 */
import {
  msgState,
  SCodeSelect,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getSCode, SCode, ShohinshubetsuKbn } from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";
import {
  B000150TokuiNm2,
  B000151NonyuNm,
  B000223BatchNm,
  B000224HaisorouteNm,
  B080080Find,
  B080080FindRequest,
  catchRestError,
} from "../../clients/rest";
import { Column, Query } from "@material-table/core";
import { useNavigate } from "react-router-dom";
import { B000223 } from "../b000/B000223";
import { B000224 } from "../b000/B000224";
import { B000150 } from "../b000/B000150";
import { B000151 } from "../b000/B000151";
import { getClientError } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  batchNo: string;
  batchNm: string;
  haisorouteNo: string;
  haisorouteNm: string;
  shohinshubetsuKbn: SCode;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNm: string;
  tenpoCd: string;
  nonyuCd: string;
  nonyushitenCd: string;
  nonyuNm: string;
}>;

type Data = Partial<{
  batchNo: string;
  batchNm: string;
  haisorouteNo: string;
  haisorouteNm: string;
  shohinshubetsuKbn: SCode;
  tokuiNm: string;
  nonyuNm: string;
  haisojuni: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  batchNo: "",
  batchNm: "",
  haisorouteNo: "",
  haisorouteNm: "",
  shohinshubetsuKbn: undefined,
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNm: "",
  nonyuCd: "",
  nonyushitenCd: "",
  nonyuNm: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "batchNo", title: "バッチ番号　", width: "2rem", align: "right" },
  { field: "batchNm", title: "バッチ名", width: "6rem" },
  {
    field: "haisorouteNo",
    title: "配送ルート番号　",
    width: "3rem",
    align: "right",
  },
  { field: "haisorouteNm", title: "配送ルート名", width: "6rem" },
  {
    field: "shohinshubetsuKbn",
    title: "商品種別",
    width: "5rem",
    render: (data) => {
      return getSCode(
        ShohinshubetsuKbn,
        data.shohinshubetsuKbn?.toString() ?? "0"
      )?.label;
    },
  },
  { field: "tokuiNm", title: "得意先名", width: "6rem" },
  { field: "nonyuNm", title: "納入先名", width: "6rem" },
  { field: "haisojuni", title: "配送順位　", width: "2.5rem", align: "right" },
];

const focusId = "B080080";

export const B080080 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  useEffect(() => {
    focus(1);
  });

  //初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  //テーブル値取得用Ref
  const tableRef = createRef(); //検索イベント

  // 検索イベント
  const handleSearch = (query: Query<any>) =>
    new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      const param: B080080FindRequest = {
        tokuiCd: fields.tokuiCd!,
        tokuishitenCd: fields.tokuishitenCd!,
        nonyuCd: fields.nonyuCd!,
        nonyushitenCd: fields.nonyushitenCd!,
        batchNo: fields.batchNo,
        haisorouteNo: fields.haisorouteNo,
        shohinshubetsuKbn: fields.shohinshubetsuKbn?.value,
        page: query.page,
        size: query.pageSize,
        sort: ["batchNo,asc"],
      };
      B080080Find(param)
        .then((value) => {
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B080080_001"));
            resolve({
              data: [],
              page: query.page,
              totalCount: 0,
            });
          } else {
            value.data.content.map(
              (v: any, i: number) =>
                (v.id = i + 1 + query.pageSize * query.page)
            );
            resolve({
              data: value.data.content,
              page: value.data.number,
              totalCount: value.data.totalElements,
            });
          }
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: query.page,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });

  const handleClickSearchButton = () => {
    //検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    //テーブル際検索
    (tableRef.current as any).onQueryChange();
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="バッチ関連照会"
        gridTemplateColumns="7rem 6rem 5rem 16rem 8rem 6rem 5rem 15rem 2rem"
      >
        <SLabel>バッチ番号</SLabel>
        <SNumberField
          name="batchNo"
          code
          size={4}
          {...register(1)}
          gridColumn="span 2"
          onBlur={() => {
            B000223BatchNm(methods, navigate, setMsg);
          }}
          dialog={<B000223 />}
          handleSubmit={(data: any) => {
            methods.setValue("batchNo", data.batchNo);
            methods.setValue("batchNm", data.batchNm);
          }}
        />
        <STextField name="batchNm" size={40} disabled />
        <SLabel>配送ルート番号</SLabel>
        <SNumberField
          name="haisorouteNo"
          code
          size={9}
          {...register(2)}
          gridColumn="span 2"
          onBlur={() => {
            B000224HaisorouteNm(methods, navigate, setMsg);
          }}
          dialog={<B000224 />}
          handleSubmit={(data: any) => {
            methods.setValue("haisorouteNo", data.haisorouteNo);
            methods.setValue("haisorouteNm", data.haisorouteNm);
          }}
        />
        <STextField name="haisorouteNm" size={40} disabled />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">商品種別</SLabel>
        <SCodeSelect
          label="商品種別"
          name="shohinshubetsuKbn"
          options={ShohinshubetsuKbn}
          empty
          {...register(3)}
          gridColumn="span 2"
        />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1" required>
          得意先コード
        </SLabel>
        <SNumberField
          name="tokuiCd"
          code
          size={6}
          onBlur={() => {
            B000150TokuiNm2(methods, navigate, setMsg);
          }}
          {...register(4)}
        />
        <SNumberField
          name="tokuishitenCd"
          code
          size={4}
          {...register(5)}
          onBlur={() => {
            B000150TokuiNm2(methods, navigate, setMsg);
          }}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNm", data.tokuiNmr);
          }}
        />
        <STextField name="tokuiNm" size={40} disabled />
        <SLabel required>納入先コード</SLabel>
        <SNumberField
          name="nonyuCd"
          code
          size={6}
          onBlur={() => {
            B000151NonyuNm(methods, navigate, setMsg);
          }}
          {...register(6)}
        />
        <SNumberField
          name="nonyushitenCd"
          code
          size={4}
          {...register(7)}
          onBlur={() => {
            B000151NonyuNm(methods, navigate, setMsg);
          }}
          dialog={<B000151 />}
          handleSubmit={(data: any) => {
            methods.setValue("nonyuCd", data.nonyuCd);
            methods.setValue("nonyushitenCd", data.nonyushitenCd);
            methods.setValue("nonyuNm", data.tokuiNmr);
          }}
        />
        <STextField name="nonyuNm" size={40} disabled />
        <SNormalButton
          label={labels.buttons.search}
          gridRow={6}
          gridColumn={9}
          {...register(8)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          width={1280}
        />
      </SFrame>
    </FormProvider>
  );
};
